import { getProductInventoryPairs } from '@libs/api-proxy-client';
import { PageType, usePageLayout } from '@libs/common-next';
import type { Inventory } from '@libs/common-node';
import type { DisplayStore, SearchStore } from '@libs/common-types';
import { getLocale, Locale, logger, stringifyStoreId } from '@libs/core';

import { hasStock, validateStockStatus } from '../../utils/stockStatusUtils';
import type { GeolocationResponse, UserLocation } from './types';

type AlertTextType = {
    title: string;
    text: string | React.ReactNode;
    link?: string;
    linkText?: string;
};

const GENERAL_ALERT: AlertTextType = {
    title: 'No locations found',
    text: 'Please try again',
};

export const getAlertText = (searchText: string): AlertTextType => {
    const isCurrentLocaleUS = getLocale() !== Locale['en-CA'];

    if (searchText === '') {
        return GENERAL_ALERT;
    }

    // check if usser is trying to search an US postal code on the .ca site
    if (!isCurrentLocaleUS) {
        const isUSPostalCode = /^\d{5}(?:[-\s]\d{4})?$/.test(searchText);

        if (isUSPostalCode) {
            return {
                title: 'Searching for a store in the United States?',
                text: 'Please go to ',
                link: 'https://www.petsmart.com',
                linkText: 'petsmart.com',
            };
        }
    }

    // check if user is trying to search Canadian postal codes on the .com site
    if (isCurrentLocaleUS) {
        const isCAPostalCode = /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVXY][ -]?\d[ABCEGHJKLMNPRSTVXY]\d$/i.test(
            searchText
        );

        if (isCAPostalCode) {
            return {
                title: 'Searching for a store in Canada?',
                text: 'Please go to ',
                link: 'https://www.petsmart.ca',
                linkText: 'petsmart.ca',
            };
        }
    }

    return GENERAL_ALERT;
};

const storeInventoryPart = ({
    store,
    inventory,
    minimumCaseQty = 1,
}: {
    store: SearchStore;
    inventory: Inventory;
    minimumCaseQty?: number;
}): Partial<DisplayStore> => {
    const storeInventory = inventory[stringifyStoreId(store.Store.StoreNumber) as string] as number | undefined;

    const currentStoreStockstatus = validateStockStatus({
        stockQuantity: storeInventory as number,
        minimumCaseQty,
    });

    return {
        CurrentProductStockstatus: currentStoreStockstatus,
        HasCurrentProductInStock: hasStock(currentStoreStockstatus) ? storeInventory : 0,
    };
};

export const addStoreSkuInventoryToResult = (
    storeInventory: Inventory,
    storeList: SearchStore[],
    minimumCaseQty?: number
) =>
    storeList.map((s: SearchStore) => ({
        ...s,
        Store: {
            ...s.Store,
            ...storeInventoryPart({
                store: s,
                inventory: storeInventory,
                minimumCaseQty,
            }),
        },
    }));

export const storeSearchHasStock = (s: SearchStore) =>
    s.Store?.CurrentProductStockstatus && hasStock(s.Store.CurrentProductStockstatus);

export const convertStoreIdToDemandwareFormat = (id: number) => id.toString().padStart(4, '0');

function getCurrentPositionPromise(): Geolocation | unknown {
    return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        } else {
            reject(new Error('Geolocation is not supported by this browser.'));
        }
    });
}

/*
    Safari will keep asking the user if they want to share their location if they refresh the page.
    But it does not show on page load.
*/
export const getUserLocation = async (): Promise<UserLocation> => {
    let location: UserLocation = { isGeolocationEnabled: false };
    if (navigator.permissions) {
        try {
            const result = await navigator.permissions.query({
                name: 'geolocation',
            });
            if (result.state === 'prompt' || result.state === 'granted') {
                const promise: GeolocationResponse = (await getCurrentPositionPromise()) as GeolocationResponse;
                location = {
                    latitude: promise?.coords?.latitude,
                    longitude: promise?.coords?.longitude,
                    isGeolocationEnabled: true,
                };
            }
        } catch (e) {
            console.log('User denied location services', e);
        }
    }
    console.error('Permissions API is not supported in this browser.');
    return location;
};

type StoreSku = { store: string; sku: string };
export const getStoresWhereInStock = async (storeList: SearchStore[], sku: number | string) => {
    try {
        const storesku: StoreSku[] = storeList.reduce<StoreSku[]>((acc, store) => {
            if (store?.Store?.StoreNumber) {
                acc.push({
                    store: `${store?.Store?.StoreNumber}`,
                    sku: `${sku}`,
                });
            }
            return acc;
        }, []);

        const storesWhereInStock = await getProductInventoryPairs(storesku);

        return storesWhereInStock.find(inventory => inventory.sku === String(sku)) ?? ({} as Inventory);
    } catch (error) {
        logger.error('Error getStoresWhereInStock: %o', error);

        return {} as Inventory;
    }
};

export const enhanceStoreListWithInventory = async (
    storeList: SearchStore[],
    sku?: number | string,
    currentBopisMinimumCaseQty?: number
) => {
    if (!sku) {
        return storeList;
    }

    const inventory = await getStoresWhereInStock(storeList, sku);

    return addStoreSkuInventoryToResult(inventory, storeList, currentBopisMinimumCaseQty);
};

export const useSearchDrawerSku = (pageType?: string) => {
    const { cartStoreDrawerOpen, currentVariantId } = usePageLayout();

    if (pageType === PageType.CART) {
        const sku = cartStoreDrawerOpen.item?.sku;
        return sku ? Number(sku) : undefined;
    }

    if (pageType === PageType.PDP) {
        return currentVariantId > 0 ? currentVariantId : undefined;
    }

    return undefined;
};

export const filterStoreListToShow = (storeList: SearchStore[], showInventoryOnly: boolean) => {
    if (showInventoryOnly) {
        return storeList.filter(s => Boolean(s.Store.HasCurrentProductInStock));
    }
    return storeList;
};

/** PLP: pre-select the user's global store when it appears in the search list (BOPIS or not). */
export const resolvePreferredPickedStoreNumber = (
    storeList: SearchStore[],
    preferredStoreNumber: number | undefined
): number | null => {
    if (!preferredStoreNumber || !storeList.length) {
        return null;
    }

    const preferred = storeList.find(s => s.Store.StoreNumber === preferredStoreNumber);

    return preferred ? preferredStoreNumber : null;
};

export type ResolvePlpPickedStoreNumberSyncParams = {
    storeList: SearchStore[];
    globalStoreNumber: number;
    pickedStore: number | null;
    globalStoreChanged: boolean;
    isStoreSelectable: (store: SearchStore) => boolean;
};

/** PLP drawer pick sync: next value, or undefined to leave pickedStore unchanged. */
export const resolvePlpPickedStoreNumberSync = ({
    storeList,
    globalStoreNumber,
    pickedStore,
    globalStoreChanged,
    isStoreSelectable,
}: ResolvePlpPickedStoreNumberSyncParams): number | null | undefined => {
    if (!storeList.length) {
        return undefined;
    }

    const preferred = resolvePreferredPickedStoreNumber(storeList, globalStoreNumber);

    if (pickedStore == null) {
        return preferred ?? undefined;
    }

    const pickedInList = storeList.some(s => s.Store.StoreNumber === pickedStore);
    if (!pickedInList) {
        return preferred;
    }

    if (globalStoreChanged) {
        return preferred ?? undefined;
    }

    if (pickedStore === globalStoreNumber) {
        return undefined;
    }

    const pickedSearchStore = storeList.find(s => s.Store.StoreNumber === pickedStore);
    if (pickedSearchStore && isStoreSelectable(pickedSearchStore)) {
        return undefined;
    }

    return preferred ?? undefined;
};
